import { useEffect, useMemo, useState } from 'react';
import { t } from 'i18next';
import { addMonths } from 'date-fns';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { AxiosError, AxiosInstance } from 'axios';
import { jwtDecode } from 'jwt-decode';
import * as Accordion from '@radix-ui/react-accordion';
import * as Yup from 'yup';

import i18n from '../../../../plugins/i18n/i18n';

import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../../../DashboardCustomer/Registering/components/RadixAccordion';

import { Stepper } from '../../../../components/Stepper';
import { CustomInput } from '../../../../components/inputs';
import { Input, PageHeadline } from '../../../../components/atoms';
import { FormPhoneInput } from '../../../../components/Form/FormPhoneInput';
import { MainCustomButton } from '../../../../components/Buttons';
import { OnboardingHeader } from '../../../../components/OnboardingHeader';

import { useStepperContext } from '../../../../components/Stepper/Context';
import { hasFileName, validateFileSize, validateFileType } from '../../../../utils/fileValidation';
import { errorToast } from '../../../../utils/toasts';

import { CompanyRegisteringService } from '../../../../services/CompanyRegisteringService';

import { useAuth } from '../../../../store/auth';
import { useAppSelector } from '../../../../hooks/hooksRedux';
import { RootState } from '../../../../store';

import { errorMessageAdapter } from '../../../../adapters';
import { countries } from '../../../../constants/objects';
import { AiOutlineCheckCircle } from 'react-icons/ai';
import { useApi } from '../../../../services/hooks/apiHook';
import { defaultBorder, defaultColors } from '../../../../styles/style-guide';
import { StyledLabel, StyledSelect } from '../styleGuide';
import { FiUploadCloud } from 'react-icons/fi';
import { randomInt } from '../../../../utils/random';

type JwtPayload = {
  company_id: number;
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  nbf: number;
  prv: string;
  sub: string;
};

interface RegisterDirectorTransferFormSchema {
  name: string;
  gender: string;
  date_of_birth: string;
  ddi: string;
  ddd: string;
  number: string;
  email: string;
  nationality: string;
  passport_number: string;
  passport_expiry_date: string;
  passport_issuing_country: string;
  passport_issuing_date: string;
  address: string;
  complement: string;
  postal_code: string;
  city: string;
  state: string;
  neighborhood: string;
  country: string;
  passport: File | undefined;
  prove_address: File | undefined;
  number_of_shares: string | undefined;
  document1: File | undefined;
  is_director: string;
  is_underage: string;
  occupation: string;
}

const today = new Date();
const sixMonthsFromNow = addMonths(today, 6);
const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());

export const ShareholdersPage = (props: any) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [numberRegistredShareholders, setNumberRegistredShareholders] = useState<number>(0);
  const [numberOfShareholder, setNumberOfShareholders] = useState<number>();
  const [currentAccordion, setCurrentAccordion] = useState<string>('item-0');

  const [fileNamePassport, setFileNamePassport] = useState<File>();
  const [fileNameDocument1, setFileNameDocument1] = useState<File>();
  const [fileNameProveAddress, setFileNameProveAddress] = useState<File>();

  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);
  const { colors, border } = useAppSelector((state: RootState) => state.styleGuide.data);

  const primaryColor = colors.primary || defaultColors.primary;
  const primaryBorderColor = border.primary || defaultBorder.primary;
  const randomImage = useMemo(() => randomInt(1, 5), []);

  const { token } = useAuth();

  const stepperContext = useStepperContext();
  const api: AxiosInstance = useApi();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('validations.valRequired') || ''),
    gender: Yup.string().required(t('validations.valRequired') || ''),
    date_of_birth: Yup.date()
      .typeError(t('validations.valInvalidDate') || '')
      .max(eighteenYearsAgo, t('validations.valMinAge') || '')
      .required(t('validations.valRequired') || ''),
    ddi: Yup.string().required(t('validations.valRequired') || ''),
    number: Yup.string().required(t('validations.valRequired') || ''),
    email: Yup.string()
      .required(t('validations.valRequired') || '')
      .email(t('OnboardingCreateAccountErrorEmailMustBe') || ''),
    nationality: Yup.string().required(t('validations.valRequired') || ''),
    passport_number: Yup.string().required(t('validations.valRequired') || ''),
    passport_expiry_date: Yup.date()
      .typeError(t('validations.valInvalidDate') || '')
      .min(sixMonthsFromNow, t('validations.valExpiryDate') || '')
      .required(t('validations.valRequired') || ''),
    passport_issuing_country: Yup.string().required(t('validations.valRequired') || ''),
    passport_issuing_date: Yup.string().required(t('validations.valRequired') || ''),
    address: Yup.string().required(t('validations.valRequired') || ''),
    complement: Yup.string().nullable(),
    postal_code: Yup.string().required(t('validations.valRequired') || ''),
    city: Yup.string().required(t('validations.valRequired') || ''),
    state: Yup.string().required(t('validations.valRequired') || ''),
    country: Yup.string().required(t('validations.valRequired') || ''),
    neighborhood: Yup.string().required(t('validations.valRequired') || ''),
    passport: Yup.mixed()
      .required(t('validations.valRequired') || '')
      .test('required', t('validations.valRequired') || '', hasFileName)
      .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', validateFileSize)
      .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', validateFileType),
    prove_address: Yup.mixed()
      .test('required', t('validations.valRequired') || '', hasFileName)
      .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', validateFileSize)
      .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', validateFileType),
    document1: Yup.mixed()
      .test('required', t('validations.valRequired') || '', hasFileName)
      .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', validateFileSize)
      .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', validateFileType),
    occupation: Yup.string().required(t('validations.valRequired') || '')
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    control,
    reset
  } = useForm<RegisterDirectorTransferFormSchema>({
    resolver: yupResolver(validationSchema)
  });

  const handleFileChangePassport = (file?: File) => {
    if (file) {
      setFileNamePassport(file);
      setValue('passport', file);
    }
  };

  const handleFileChangeDocument1 = (file?: File) => {
    if (file) {
      setFileNameDocument1(file);
      setValue('document1', file);
    }
  };

  const handleFileChangeProveAddress = (file?: File) => {
    if (file) {
      setFileNameProveAddress(file);
      setValue('prove_address', file);
    }
  };

  const UploadCard = ({
    label,
    inputId,
    fileName,
    errorMessage,
    onFileSelect,
    registerName
  }: {
    label: string;
    inputId: string;
    fileName?: string;
    errorMessage?: string;
    onFileSelect: (file?: File) => void;
    registerName: 'passport' | 'document1' | 'prove_address';
  }) => (
    <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{label}</p>
          <p className="text-xs text-slate-500">{t('labels.lblDocumentsSupported')}</p>
        </div>
        {fileName ? (
          <span className="max-w-[180px] truncate rounded-full bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
            {fileName}
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500">PDF/DOCX</span>
        )}
      </div>
      <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 transition hover:border-blue-300 hover:bg-blue-50/40">
        <div className="flex items-center gap-3 text-slate-600">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-blue-600">
            <FiUploadCloud size={18} />
          </span>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-slate-700">{t('labels.lblPushYourDocument')}</span>
            <span className="text-xs text-slate-500">{t('labels.lblOrSearchInYourComputer')}</span>
          </div>
        </div>
        <span className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white">
          {t('labels.lblPushYourDocument')}
        </span>
        <input
          id={inputId}
          type="file"
          className="hidden"
          accept="image/*,.pdf"
          {...register(registerName)}
          onChange={(e) => onFileSelect(e.target.files?.[0])}
        />
      </label>
      {errorMessage ? <span className="text-xs text-red-500">{errorMessage}</span> : null}
    </div>
  );

  const headers = { 'Content-Language': legalPersonalitySlug, Authorization: `Bearer ${token}` };

  const handleSubmitForm: SubmitHandler<RegisterDirectorTransferFormSchema> = async (data: any) => {
    if (!token) return;

    const decodedAccessToken = jwtDecode<JwtPayload>(token);
    const getDDDForNumberValue = data.number.match(/\((.*)\)/)?.pop();

    try {
      setLoading(true);

      await CompanyRegisteringService.registerDirectorsCompany(
        api,
        {
          ...data,
          ddd: getDDDForNumberValue,
          number_of_shares: numberOfShareholder,
          is_director: '0',
          is_underage: '0'
        },
        decodedAccessToken.company_id,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setLoading(false);
      reset();
      setFileNamePassport(undefined);
      setFileNameDocument1(undefined);
      setFileNameProveAddress(undefined);

      const nextRegistered = numberRegistredShareholders + 1;
      setNumberRegistredShareholders(nextRegistered);

      if (numberOfShareholder !== undefined && nextRegistered >= numberOfShareholder) {
        stepperContext.nextStep();
        return;
      }

      setCurrentAccordion(`item-${nextRegistered}`);
    } catch (error) {
      setLoading(false);
      errorToast(errorMessageAdapter(error as AxiosError));
      console.error('Error fetch in registerDirectorsCompany', error);
    }
  };

  // Delaware: number_of_director fica 0 no backend — o accordion DEVE usar number_of_shareholder.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await CompanyRegisteringService.getConfigurationPlanCompany(api, { headers });
        if (data) {
          setNumberOfShareholders(Number(data.number_of_shareholder) || 0);
          setCurrentAccordion(`item-${numberRegistredShareholders}`);
        }
      } catch (e) {
        errorToast(errorMessageAdapter(e as AxiosError));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 lg:flex">
      <div className="w-full box-border lg:pr-[20vw]">
        <div className="px-6 py-8 lg:px-12">
          <div className="mx-auto w-full max-w-5xl">
            <OnboardingHeader showLogoutButton />

            <div className="mt-6">
              <PageHeadline
                title={t('companyTransfer.othTitlePageShareholders')}
                className="mt-8 shadow-xl"
                gradientFrom="from-primary"
                gradientTo="to-blue-800"
              />
            </div>

            <div className="mt-6">
              <Stepper currentStep={4} maxSteps={5} />
            </div>

            <div className="mt-8 rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-900/5">
              {numberOfShareholder ? (
            <Accordion.Root
              className="rounded-md bg-mauve6 shadow-[0_2px_10px] shadow-black/5"
              type="single"
              defaultValue={currentAccordion}
              value={currentAccordion}
              onValueChange={(value) => setCurrentAccordion(value)}
              collapsible
            >
              {[...Array(numberOfShareholder)].map((_, index) => {
                const directorIndex = index + 1;
                const displayIndex = directorIndex < 10 ? `0${directorIndex}` : directorIndex;

                return (
                  <form onSubmit={handleSubmit(handleSubmitForm)} id={`form-company-transfer-shareholders-${index}`} key={index}>
                    <AccordionItem
                      value={`item-${index}`}
                      disabled={!(props.forceAccordionActivated || index === numberRegistredShareholders)}
                    >
                      <AccordionTrigger textColor={primaryColor}>{`${displayIndex} ${t(
                        'labels.lblShareholder'
                      )}`}</AccordionTrigger>
                      <AccordionContent>
                        <div className="-mt-3.5 bg-white">
                          <div className="flex flex-col items-start justify-between gap-4 md:flex-row">
                            <div className="mt-6 w-full border-[1px] border-[#E1E9EF] bg-white placeholder:rounded md:w-6/12">
                              <span className="block w-full border-b-[1px] p-3 text-lg font-medium">
                                {t('labels.lblShareholder')}
                              </span>
                              <div className="flex flex-col gap-4 p-3">
                                <span className="font-bold">{t('labels.lblShareholder')}</span>
                                <CustomInput
                                  label={t('labels.lblFirstName')}
                                  id="name"
                                  register={register}
                                  error={errors?.name?.message?.toString()}
                                />
                                <CustomInput
                                  label={t('labels.lblOccupation') || ''}
                                  id="occupation"
                                  register={register}
                                  error={errors?.occupation?.message?.toString()}
                                />
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex w-full flex-col gap-1">
                                    <input type="hidden" id={'index'} value={index} />
                                    <StyledLabel
                                      $colors={primaryColor}
                                      hasError={!!errors.gender?.message}
                                      htmlFor="gender"
                                    >
                                      {t('labels.lblGender')}
                                    </StyledLabel>
                                    <StyledSelect
                                      id="gender"
                                      hasError={!!errors.gender?.message}
                                      $borderColors={primaryBorderColor}
                                      {...register('gender')}
                                      onChange={(e) => {
                                        setValue('gender', e.target.value);
                                      }}
                                    >
                                      <option value="" disabled>
                                        {t('labels.lblSelect')}
                                      </option>
                                      <option value="male">{t('labels.lblGenderMale')}</option>
                                      <option value="female">{t('labels.lblGenderFemale')}</option>
                                    </StyledSelect>
                                    <span className="h-0 text-xs text-red-500">{errors?.gender?.message?.toString()}</span>
                                  </div>
                                  <Controller
                                    name="date_of_birth"
                                    control={control}
                                    render={({ field }) => (
                                      <Input
                                        label={t('labels.lblDateOfBirth') || ''}
                                        error={errors?.date_of_birth?.message?.toString()}
                                        mask="9999/99/99"
                                        {...register('date_of_birth')}
                                        {...field}
                                        onChange={(e) => setValue('date_of_birth', e.target.value)}
                                      />
                                    )}
                                  />
                                </div>
                                <FormPhoneInput
                                  label={t('labels.lblPhone')}
                                  id="number"
                                  name="number"
                                  error={errors?.number?.message?.toString()}
                                  onPhoneChange={([ddi, phone]) => {
                                    setValue('ddi', ddi);
                                    setValue('number', phone);
                                  }}
                                  control={control}
                                />

                                <CustomInput
                                  label={t('labels.lblEmail')}
                                  id="email"
                                  register={register}
                                  error={errors?.email?.message?.toString()}
                                />
                                <div className="flex w-full flex-col gap-1">
                                  <input type="hidden" id={'index'} value={index} />
                                  <StyledLabel
                                    htmlFor="nationality"
                                    hasError={!!errors.nationality?.message}
                                    $colors={primaryColor}
                                  >
                                    {t('labels.lblNationality')}
                                  </StyledLabel>
                                  <StyledSelect
                                    id="nationality"
                                    hasError={!!errors.nationality?.message}
                                    $borderColors={primaryBorderColor}
                                    {...register('nationality')}
                                    onChange={(e) => setValue('nationality', e.target.value)}
                                  >
                                    <option value="" disabled>
                                      {t('labels.lblSelect')}
                                    </option>
                                    {countries[i18n.language as 'pt-BR'].map((country) => (
                                      <option key={country.name} value={country.name}>
                                        {country.name}
                                      </option>
                                    ))}
                                  </StyledSelect>
                                  <span className="h-0 text-xs text-red-500">
                                    {errors?.nationality?.message?.toString()}
                                  </span>
                                </div>
                                <div className="flex items-start justify-between gap-2">
                                  <CustomInput
                                    label={t('labels.lblPassportNo')}
                                    id="passport_number"
                                    register={register}
                                    error={errors?.passport_number?.message?.toString()}
                                  />

                                  <Controller
                                    name="passport_expiry_date"
                                    control={control}
                                    render={({ field }) => (
                                      <Input
                                        label={t('labels.lblExpiryDate') || ''}
                                        error={errors?.passport_expiry_date?.message?.toString()}
                                        mask="9999/99/99"
                                        {...register('passport_expiry_date')}
                                        {...field}
                                        onChange={(e) => setValue('passport_expiry_date', e.target.value)}
                                      />
                                    )}
                                  />
                                 
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex w-full flex-col gap-1">
                                    <input type="hidden" id={'index'} value={index} />
                                    <StyledLabel
                                      htmlFor="passport_issuing_country"
                                      hasError={!!errors.passport_issuing_country?.message}
                                      $colors={primaryColor}
                                    >
                                      {t('labels.lblPassportIssuingCountry')}
                                    </StyledLabel>
                                    <StyledSelect
                                      id="passport_issuing_country"
                                      hasError={!!errors.passport_issuing_country?.message}
                                      $borderColors={primaryBorderColor}
                                      {...register('passport_issuing_country')}
                                      onChange={(e) => setValue('passport_issuing_country', e.target.value)}
                                    >
                                      <option value="" disabled>
                                        {t('labels.lblSelect')}
                                      </option>
                                      {countries[i18n.language as 'pt-BR'].map((country) => (
                                        <option key={country.name} value={country.name}>
                                          {country.name}
                                        </option>
                                      ))}
                                    </StyledSelect>
                                    <span className="h-0 text-xs text-red-500">
                                      {errors?.passport_issuing_country?.message?.toString()}
                                    </span>
                                  </div>
                                  <Controller
                                    name="passport_issuing_date"
                                    control={control}
                                    render={({ field }) => (
                                      <Input
                                        label={t('labels.lblIssueDate') || ''}
                                        error={errors?.passport_issuing_date?.message?.toString()}
                                        mask="9999/99/99"
                                        {...register('passport_issuing_date')}
                                        {...field}
                                        onChange={(e) => setValue('passport_issuing_date', e.target.value)}
                                      />
                                    )}
                                  />
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <CustomInput
                                    label={t('labels.lblAddress1')}
                                    id="address"
                                    register={register}
                                    error={errors?.address?.message?.toString()}
                                  />
                                  <CustomInput
                                    label={t('companyTransfer.othComplementTransfer')}
                                    id="complement"
                                    register={register}
                                  />
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <CustomInput
                                    label={t('labels.lblPostalCode')}
                                    id="postal_code"
                                    register={register}
                                    error={errors?.postal_code?.message?.toString()}
                                  />
                                  <CustomInput
                                    label={t('labels.lblCity')}
                                    id="city"
                                    register={register}
                                    error={errors?.city?.message?.toString()}
                                  />
                                  <CustomInput
                                    label={t('labels.lblState')}
                                    id="state"
                                    register={register}
                                    error={errors?.state?.message?.toString()}
                                  />
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <CustomInput
                                    label={t('labels.lblNeighborhood')}
                                    id="neighborhood"
                                    register={register}
                                    error={errors?.neighborhood?.message?.toString()}
                                  />
                                  <div className="flex w-full flex-col gap-1">
                                    <input type="hidden" id={'index'} value={index} />
                                    <StyledLabel
                                      htmlFor="country"
                                      hasError={!!errors.country?.message}
                                      $colors={primaryColor}
                                    >
                                      {t('labels.lblCountry')}
                                    </StyledLabel>
                                    <StyledSelect
                                      id="country"
                                      hasError={!!errors.country?.message}
                                      $focusColor={'#1d4ed8'}
                                      $borderColors={primaryBorderColor}
                                      {...register('country')}
                                      onChange={(e) => setValue('country', e.target.value)}
                                    >
                                      <option value="" disabled>
                                        {t('labels.lblSelect')}
                                      </option>
                                      {countries[i18n.language as 'pt-BR'].map((country) => (
                                        <option key={country.name} value={country.name}>
                                          {country.name}
                                        </option>
                                      ))}
                                    </StyledSelect>
                                    <span className="h-0 text-xs text-red-500">{errors?.country?.message?.toString()}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="mt-6 w-full border-[1px] border-[#E1E9EF] bg-white placeholder:rounded md:w-6/12">
                              <span className="block w-full border-b-[1px] p-3 text-lg font-medium">
                                {t('labels.lblUploadShareholdersDocuments')}{' '}
                              </span>
                          <div className="flex flex-col gap-4 p-3">
                            <UploadCard
                              label={`${t('labels.lblPassport')} - ${t('labels.lblShareholder')} ${displayIndex}`}
                              inputId={`passport-${displayIndex}`}
                              fileName={fileNamePassport?.name}
                              errorMessage={errors.passport?.message?.toString()}
                              registerName="passport"
                              onFileSelect={handleFileChangePassport}
                            />
                            <UploadCard
                              label={`${t('labels.lblLocalDocument')} - ${t('labels.lblShareholder')} 1`}
                              inputId={`document1-${displayIndex}`}
                              fileName={fileNameDocument1?.name}
                              errorMessage={errors.document1?.message?.toString()}
                              registerName="document1"
                              onFileSelect={handleFileChangeDocument1}
                            />
                            <UploadCard
                              label={`${t('labels.lblProveAddress')} - ${t('labels.lblShareholder')} 1`}
                              inputId={`prove-address-${displayIndex}`}
                              fileName={fileNameProveAddress?.name}
                              errorMessage={errors.prove_address?.message?.toString()}
                              registerName="prove_address"
                              onFileSelect={handleFileChangeProveAddress}
                            />
                          </div>
                            </div>
                          </div>
                        </div>
                        <div className="bottom-6 mt-10 flex justify-end gap-4 py-4">
                          <MainCustomButton
                            text={t('labels.lblNextStep')}
                            form={`form-company-transfer-shareholders-${index}`}
                            loading={loading}
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </form>
                );
              })}
            </Accordion.Root>
          ) : (
            <div className="flex w-full flex-col items-center justify-center gap-1 rounded-lg bg-[#f8f8f8] p-4 shadow-lg">
              <div className="flex items-center gap-2 pt-8">
                <AiOutlineCheckCircle color="#06C051" size={24} />
                <h4 className="text-md font-semibold text-[#000000]">{`${t(
                  'companyTransfer.othWarningDontRegisterCompany'
                )}`}</h4>
              </div>
              <div className="bottom-6 flex flex justify-end gap-4 pb-4 pt-8">
                <MainCustomButton
                  className="p-3"
                  text={t('labels.lblNextStep')}
                  loading={loading}
                  onClick={() => stepperContext.nextStep()}
                />
              </div>
            </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="fixed right-0 top-0 hidden h-screen lg:block lg:w-1/5">
        <div className="relative h-full">
          <img src={`/img/image0${randomImage}.png`} className="h-full w-full object-cover" alt="" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
          <p className="absolute bottom-[25%] left-6 right-6 text-[32px] font-bold leading-tight text-white">
            {t('labels.lblWeveHelped')}
          </p>
        </div>
      </div>
    </div>
  );
};
